/* eslint-disable react/no-unused-prop-types */ // ! could not find a workaround

import { CSSProperties, FC, PropsWithChildren, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as gql from "gql-query-builder";
import { isEqual } from "lodash";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AxiosHeaders, AxiosRequestConfig } from "axios";
import {
    AgGridEvent,
    ColDef,
    ColumnVisibleEvent,
    FilterChangedEvent,
    FilterModel,
    FirstDataRenderedEvent,
    GridApi,
    GridOptions,
    GridReadyEvent,
    IServerSideGetRowsParams,
    IServerSideGetRowsRequest,
    GridPreDestroyedEvent,
} from "ag-grid-enterprise";
import AgGrid from "./components/AgGrid";
import HeaderAndButton from "./components/HeaderAndFilter";
import {
    BO_CORE_DEFAULT_VALUE,
    GENERIC_PRO_GRID_DEFAULT_AVAILABLE_PAGE_SIZES,
    GENERIC_PRO_GRID_DEFAULT_PAGE_SIZE,
    isCancelledErrorProcessor,
    toastError,
} from "../../utils/AppUtils";
import {
    generatingGridColumns,
    CustomTypeColumnDefinition,
    generateGqlFilterObject,
    generateGqlSort,
    renderLocalDateTime,
    booleanCellRenderer,
    defaultCellRenderer,
    idColumnCellRenderer,
    additionalLinkColumnRenderer,
    generateRestApiSortString,
    generateRestApiFilterString,
    standardParamsSerializer,
    getFilter,
    DEFAULT_COLID_DOTS,
    renderDots,
} from "../../utils/AGGridUtils";
import { Root } from "./GenericProGridStyles";
import useAbortApi from "../../Hooks/useAbortApi";
import { uiActions } from "../../reducers/uiReducer";
import { GRID_IDENTIFIERS } from "../../contexts/types";
import GenericProGridProvider, { GenericProGridContext } from "./GenericProGridContext/GenericProGridContext";
import { graphQlRequest } from "../../config/axios/apis/graphql/graphql";
import { GridCommentFlyIn } from "../Common/GridCommentFlyIn/GridCommentFlyIn";
import { RootState } from "../../store";
import { getTypeByKey } from "../../config/axios/apis/type/type";
import { ColumnHeaderCopyWrapper } from "./components/ColumnHeaderCopyWrapper/ColumnHeaderCopyWrapper";
import CopyToClipboard from "../Common/CopyToClipboard/CopyToClipboard";
import { ExportGridIdentifier } from "../../utils/appEnums/AppEnums";

export interface GenericGridColumn {
    field: string;
    headerName: string;
    cellRenderer?: (value: any, params?: any) => any;
}

interface ProGridConfig {
    tableTitle: string | ReactNode;
    showIdColumn: { show: boolean; sort?: "desc" | "asc" };
    editLink: string | ((rowData: any) => void);
    editLinkQueryParams?: object;
    editLinkNewTab?: boolean;
    additionalLinkColumns?: { columnName: string; link: string | ((rowData: any) => void); queryParams?: object; target?: string }[];
    deleteButtonFunc?: (id: number, rowData?: any) => Promise<any>;
    deactivateButtonFunc?: (id: number) => Promise<any>;
    initialShownColumns?: string[];
    ignoreBocoreAttributes?: string[];
    initialFilters?: any;
    otherColumns?: {
        key: string;
        title: string;
        type: string;
        existing?: boolean;
        cellRenderer?: (value: any, params?: any) => any;
        columnOptions?: ColDef;
        attributeValues?: any[];
    }[];
    columnOptions?: { key: string; options: ColDef }[];
    deactivateButtonActivationChecker?: string | ((rowData: any) => boolean);
    gridHeightCss?: CSSProperties["height"];
    additionalHamburgerMenuItems?: (params) => { name: string; action: () => void; disabled: boolean }[];
    fullTextSearchLabel?: ReactNode;
    fullTextSearchPlaceholder?: string;
    dynamicFilters?: object;
    exportIdentifier?: ExportGridIdentifier;
}

interface ProGridDataFetchingConfig {
    dataFetchingType: "gql" | "restApi" | "localData";
    bocoreType?: string;
    customTypeDefinition?: CustomTypeColumnDefinition[];
    data?: object[];
    restApiRequest?: any;
    restApiDataClientSide?: boolean;
    pageSize?: number;
    availablePageSizes?: number[];
    requestFilterProcessorV3?: (params: IServerSideGetRowsRequest, initialFilters?: FilterModel) => any[];
    disableSearchParamsV3?: boolean;
    // Escape hatch for the gql path: extra entries appended to the `records` selection,
    // for relationship resolution that BoCore's auto-discovered attributes don't cover.
    // Shape matches gql-query-builder field entries (string | nested-object).
    additionalGqlFields?: any[];
}

interface ProGridFeatureFlags {
    rowDragging?: boolean;
    disableFullTextSearch?: boolean;
    disableRowSelecting?: boolean;
    disableAllRowsSelecting?: boolean;
    disableEditing?: boolean;
    disableExporting?: boolean;
    disableHeaderHamburgerMenu?: boolean;
    turnOffManageColumns?: boolean;
    disableOptionMenu?: boolean;
    enableUATOrderActions?: boolean;
    enableUATJourneyActions?: boolean;
    enableViewSelector?: boolean;
    enableOrderCommentsFlyIn?: boolean;
    showTotalCount?: boolean;
    enableRefreshButton?: boolean;
    enablePageSizeSelector?: boolean;
}

interface IProps extends ProGridConfig, ProGridDataFetchingConfig, ProGridFeatureFlags {
    gridIdentifier: `${GRID_IDENTIFIERS}`;
    customGridOptions?: GridOptions;
    // lastColumnButtonLabel?: string;
    // lastColumnButtonFunc?: any;
    // dataFetchingType: "gql" | "restApi" | "localData"; // * ProGridDataFetchingConfig
    // bocoreType?: string; // * ProGridDataFetchingConfig
    // customTypeDefinition?: CustomTypeColumnDefinition[]; // * ProGridDataFetchingConfig
    // data?: {}[]; // * ProGridDataFetchingConfig
    // restApiRequest?: any; // * ProGridDataFetchingConfig
    // restApiDataClientSide?: boolean; // * ProGridDataFetchingConfig
    // pageSize: number; // * ProGridDataFetchingConfig
    // tableTitle: string | ReactNode; // ! ProGridConfig
    // showIdColumn: { show: boolean; sort?: "desc" | "asc" }; // ! ProGridConfig
    // editLink: string | Function; // ! ProGridConfig
    // editLinkQueryParams?: {}; // ! ProGridConfig
    // editLinkNewTab?: boolean; // ! ProGridConfig
    // additionalLinkColumns?: { columnName: string; link: string | Function; queryParams?: {}; target?: string }[]; // ! ProGridConfig
    // deleteButtonFunc?: (id: number) => Promise<any>; // ! ProGridConfig
    // deactivateButtonFunc?: (id: number) => Promise<any>; // ! ProGridConfig
    // initialShownColumns?: string[]; // ! ProGridConfig
    // ignoreBocoreAttributes?: string[]; // ! ProGridConfig
    // initialFilters?: any; // ! ProGridConfig
    // otherColumns?: { key: string; title: string; type: string; existing?: boolean; cellRenderer?: Function; columnOptions?: ColDef; attributeValues?: any[] }[]; // ! ProGridConfig
    // customCellRenderers?: { key: string; cellRendered: Function }[]; // ! ProGridConfig
    // columnOptions?: { key: string; options: ColDef }[]; // ! ProGridConfig
    // deactivateButtonActivationChecker?: string | ((rowData: any) => boolean); // ! ProGridConfig
    // gridHeightCss?: string; // ! ProGridConfig
    // additionalHamburgerMenuItems?: (params) => { name: string; action: () => void; disabled: boolean }[]; // ! ProGridConfig
    // rowDragging?: boolean; // ? ProGridFeatureFlags
    // lastColumnButton?: boolean; // ? ProGridFeatureFlags
    // disableFullTextSearch?: boolean; // ? ProGridFeatureFlags
    // disableRowSelecting?: boolean; // ? ProGridFeatureFlags
    // disableEditing?: boolean; // ? ProGridFeatureFlags
    // disableExporting?: boolean; // ? ProGridFeatureFlags
    // disableHeaderHamburgerMenu?: boolean; // ? ProGridFeatureFlags
    // turnOffManageColumns?: boolean; // ? ProGridFeatureFlags
    // disableOptionMenu?: boolean; // ? ProGridFeatureFlags
    /**
     *This prop represents an array of colId/key
     */
    // valueTypeConverterColumns?: string[]; // ? ProGridFeatureFlags
}

const appendStandardGridColumns = (
    gridConvertedAttributes: any[],
    opts: {
        showIdColumn: { show: boolean; sort?: any } | undefined;
        disableOptionMenu: boolean | undefined;
        dataFetchingType: string;
        editLink: any;
        editLinkNewTab: boolean | undefined;
        editLinkQueryParams: any;
        navigate: any;
    },
) => {
    if (opts.showIdColumn?.show) {
        gridConvertedAttributes.unshift({
            field: "id",
            headerName: "ID",
            cellRenderer: (value: any, params: any) =>
                idColumnCellRenderer(value, params, opts.editLinkNewTab, opts.editLink, opts.editLinkQueryParams, opts.navigate),
            filter: "agNumberColumnFilter",
            floatingFilter: true,
            minWidth: 120,
            hide: false,
            ...(opts.showIdColumn?.sort && { sort: opts.showIdColumn?.sort }),
            headerComponentParams: { innerHeaderComponent: ColumnHeaderCopyWrapper },
        });
    }
    if (!opts.disableOptionMenu) {
        gridConvertedAttributes.unshift({
            field: DEFAULT_COLID_DOTS,
            sortable: false,
            suppressColumnsToolPanel: true,
            headerName: "",
            maxWidth: 26,
            lockPinned: true,
            pinned: "left",
            lockPosition: "left",
            cellRenderer: (value: any, params: any) => renderDots(value, params),
            floatingFilter: false,
            hide: false,
            resizable: false,
            cellStyle: { padding: "0px" },
        } as ColDef);
    }
    if (opts.dataFetchingType === "gql") {
        gridConvertedAttributes.push({
            field: "-",
            hide: true,
            lockVisible: true,
            filter: "agTextColumnFilter",
            filterParams: { newRowsAction: "keep" },
            suppressColumnsToolPanel: true,
        });
    }
};

// * renamed to enable autocomplete import and intellisence
const GenericProGridImplementation: FC<Omit<IProps, "gridIdentifier">> = ({
    bocoreType,
    tableTitle,
    initialShownColumns,
    restApiDataClientSide,
    pageSize = GENERIC_PRO_GRID_DEFAULT_PAGE_SIZE,
    deleteButtonFunc,
    deactivateButtonFunc,
    editLinkQueryParams,
    gridHeightCss,
    restApiRequest,
    editLink,
    initialFilters,
    data,
    editLinkNewTab,
    dataFetchingType,
    showIdColumn,
    additionalLinkColumns,
    ignoreBocoreAttributes,
    otherColumns,
    columnOptions,
    deactivateButtonActivationChecker,
    customTypeDefinition,
    rowDragging,
    customGridOptions,
    disableFullTextSearch,
    additionalHamburgerMenuItems,
    disableRowSelecting,
    disableAllRowsSelecting,
    disableEditing,
    disableExporting,
    disableHeaderHamburgerMenu,
    turnOffManageColumns,
    disableOptionMenu,
    requestFilterProcessorV3,
    enableUATOrderActions = false,
    enableUATJourneyActions = false,
    enableViewSelector = false,
    enableOrderCommentsFlyIn = false,
    fullTextSearchLabel = "Full Text Search",
    fullTextSearchPlaceholder = "Full Text Search",
    disableSearchParamsV3 = false,
    dynamicFilters,
    showTotalCount = true,
    enablePageSizeSelector = true,
    enableRefreshButton = true,
    availablePageSizes = GENERIC_PRO_GRID_DEFAULT_AVAILABLE_PAGE_SIZES,
    exportIdentifier,
    additionalGqlFields,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { signal } = useAbortApi();

    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [gridAttributes, setGridAttributes] = useState<any[]>([]);
    const [fieldRecords, setFieldRecords] = useState([]);
    const [clientSideRowData, setClientSideRowData] = useState<any>();

    const [openComment, setOpenComment] = useState<boolean>(false);
    const [commentJourneyId, setCommentJourneyId] = useState<number | null>(null);

    const shouldGridRefresh = useSelector((store: RootState) => store.uiSliceReducer.refreshGrid);

    const { incomingGridState, resetIncomingState, setActiveGridState, setDefaultGridState } = useContext(GenericProGridContext);

    const hasUserFilterableColumns = useMemo(
        () => gridAttributes.some((col) => col.floatingFilter === true && !col.lockVisible && !col.suppressColumnsToolPanel),
        [gridAttributes],
    );

    const handleCloseCommentFlyIn = () => {
        setOpenComment(false);
    };

    const commentContextMenuAction = useCallback(
        (selectedRowData) => {
            if (!gridApi) {
                return;
            }

            if (selectedRowData?.journeyId) {
                setOpenComment(true);
                setCommentJourneyId(selectedRowData?.journeyId);
            }
        },
        [gridApi],
    );

    useEffect(() => {
        if (!dynamicFilters || !gridApi) {
            return;
        }

        gridApi.setFilterModel({ ...gridApi.getFilterModel(), ...dynamicFilters });
    }, [dynamicFilters]);

    useEffect(() => {
        if (!incomingGridState || !gridApi) {
            return;
        }

        const currentFilterModel = gridApi.getFilterModel();
        const currentPivotMode = gridApi.isPivotMode();
        const currentColumnState = gridApi.getColumnState();
        const currentColumnGroupState = gridApi.getColumnGroupState();

        const mergedFilterModel = { ...initialFilters, ...incomingGridState.filterModel };
        if (initialFilters) {
            Object.keys(initialFilters).forEach((key) => {
                const col = gridApi.getColumn(key);
                const colDef = col?.getColDef();
                const isPermanentlyHidden = col && !col.isVisible() && (colDef?.lockVisible || colDef?.suppressColumnsToolPanel);
                if (isPermanentlyHidden) {
                    mergedFilterModel[key] = initialFilters[key];
                }
            });
        }
        if (!isEqual(currentFilterModel, mergedFilterModel)) {
            setTimeout(() => {
                gridApi.setFilterModel(mergedFilterModel);
            }, 100);
        }

        if (!isEqual(currentPivotMode, incomingGridState.pivotMode)) {
            setTimeout(() => {
                gridApi.setGridOption("pivotMode", incomingGridState.pivotMode);
            }, 100);
        }

        if (!isEqual(currentColumnGroupState, incomingGridState.columnGroupState)) {
            setTimeout(() => {
                gridApi.setColumnGroupState(incomingGridState.columnGroupState);
            }, 100);
        }

        if (!isEqual(currentColumnState, incomingGridState.columnState)) {
            // fix to sync any existing view to the current grid state on search-params change
            const columnStateMap = Object.assign({}, ...incomingGridState.columnState.map((e) => ({ [e.colId]: e })));
            const incomingColumnOrder: string[] = incomingGridState.columnState.map((e) => e.colId);

            const syncedGridAttributes = gridAttributes
                .map((e) => {
                    const keys = Object.keys(e).map((key) => {
                        try {
                            // colId is with higher priority.
                            // colId can be undefined in case not configured explicitly.
                            const colKey = e.colId || e.field;
                            const item = columnStateMap[colKey][key];

                            if (Object.hasOwn(columnStateMap[colKey], key)) {
                                return { [key]: item };
                            }
                            return { [key]: e[key] };
                        } catch {
                            return { [key]: e[key] };
                        }
                    });

                    return Object.assign({}, ...keys);
                })
                .sort((a, b) => incomingColumnOrder.indexOf(a.colId || a.field) - incomingColumnOrder.indexOf(b.colId || b.field));

            // * note: check NG-2802, NG-6563 and NG-6574
            setTimeout(() => {
                gridApi.applyColumnState({
                    state: incomingGridState.columnState,
                    applyOrder: true,
                });
                setTimeout(() => {
                    setGridAttributes(syncedGridAttributes);

                    setTimeout(() => {
                        gridApi.deselectAll();
                    }, 10);
                }, 10);
            }, 100);
        }

        resetIncomingState();
        // gridApi.deselectAll();
    }, [incomingGridState, gridApi]);

    const getCellRendererByType = (value: any, rowData: any, atrbType: string, columnName: any) => {
        // CHECK IF THE COLUMN IS SPECIFIED AS A LINK
        const additionalLinkColumn = additionalLinkColumns?.find((obj) => obj.columnName === columnName);
        if (additionalLinkColumn) {
            return <CopyToClipboard>{additionalLinkColumnRenderer(value, rowData, additionalLinkColumn, navigate)}</CopyToClipboard>;
        }
        if (atrbType === "date" || atrbType === "timestamp") {
            return renderLocalDateTime(value);
        }
        if (atrbType === "boolean") {
            return <CopyToClipboard>{booleanCellRenderer(value)}</CopyToClipboard>;
        }
        return defaultCellRenderer(value);
    };

    const restApiFetching = (params: IServerSideGetRowsParams) => {
        if (!restApiRequest) {
            return null;
        }

        let sortingArray: any;
        let restApiFts: any;
        let filterString: any;

        if (!disableSearchParamsV3) {
            sortingArray = generateRestApiSortString(params?.request?.sortModel);
            const processed = generateRestApiFilterString(params?.request?.filterModel);
            const contextFts = params?.api?.getGridOption("context")?.restApiFts;
            restApiFts = processed.restApiFts ?? (contextFts ? { filter: contextFts } : undefined);
            filterString = processed.filterString;
        } else if (params?.request?.filterModel && Object.keys(params?.request?.filterModel).includes("isUat")) {
            // @ts-ignore
            filterString = generateRestApiFilterString({ isUat: params.request.filterModel.isUat })?.filterString;
        }

        const cacheBlockSize = params.api.getGridOption("cacheBlockSize") || pageSize;

        const page = params.request.endRow! / cacheBlockSize - 1;
        const requestParams = {
            filter: filterString,
            sort: sortingArray,
            page,
            limit: cacheBlockSize,
            size: cacheBlockSize,
            ...(restApiFts?.filter && { query: restApiFts?.filter }),
        };

        return restApiRequest({
            signal,
            params: requestParams,
            paramsSerializer: {
                serialize: (queryParams) => standardParamsSerializer(queryParams),
            },
            transformRequest: (bodyData, headers: AxiosHeaders) => {
                const dataCopy = typeof bodyData === "string" ? JSON.parse(bodyData) : { ...bodyData };
                const isV3Api = headers.get("x-path") === "v3";

                if (isV3Api) {
                    dataCopy.filterBy = [
                        ...(dataCopy.filterBy ? dataCopy.filterBy : []),
                        ...(typeof requestFilterProcessorV3 === "function" ? requestFilterProcessorV3(params.request, initialFilters) : []),
                    ];
                    dataCopy.orderBy = [
                        ...(dataCopy.orderBy ? dataCopy.orderBy : []),
                        ...params.request.sortModel.map((e) => ({
                            key: e.colId,
                            order: e.sort.toUpperCase(),
                        })),
                    ];

                    headers.delete("x-path");
                }

                headers.set("Content-Type", "application/json");

                return JSON.stringify(dataCopy);
            },
        } as AxiosRequestConfig)
            .then((response) => {
                const { data: restApiData } = response;

                // dispatch({
                //     type: actions.NEW_DATA_FETCHED_PRO_GRID,
                //     newGridData: { startRow: params?.request?.startRow, endRow: params?.request?.endRow },
                // });
                if (gridApi?.isDestroyed()) {
                    return undefined;
                }

                dispatch(uiActions.setNewGridDataFetched({ startRow: params?.request?.startRow, endRow: params?.request?.endRow }));

                params.api.setGridOption("context", { ...params.api.getGridOption("context"), totalElementsCount: restApiData?.totalElements });

                return params.success({
                    rowData: restApiData?.data,
                    rowCount: restApiData?.totalElements,
                });
            })
            .catch(() => params.fail());
    };

    const restApiFetchingClientSide = async (_gridApiRestFetching: any) => {
        if (restApiRequest) {
            gridApi?.setGridOption("loading", true);
            restApiRequest({ signal })
                .then((res: any) => {
                    gridApi?.setGridOption("context", { ...gridApi.getGridOption("context"), totalElementsCount: res?.data?.totalElements });
                    setClientSideRowData(res?.data?.data);
                    gridApi?.setGridOption("loading", false);
                })
                .catch(() => {
                    gridApi?.setGridOption("loading", false);
                    toastError(<FormattedMessage id="common.error.api-message" defaultMessage="Something went wrong!" />);
                });
        }
    };

    const gqlFetching = (params: any) => {
        let gqlSorting: any[] = [];
        let gqlFilter = {};
        if (params?.request?.sortModel?.length > 0) {
            gqlSorting = generateGqlSort(params?.request?.sortModel);
        }
        if (params?.request?.filterModel) {
            gqlFilter = { ...generateGqlFilterObject(params?.request?.filterModel) };
        }

        const query = gql.query({
            operation: `${bocoreType}`,
            variables: {
                page: {
                    value: {
                        limit: pageSize,
                        offset: params?.request?.startRow,
                    },
                    type: `${bocoreType}PageInput`,
                },
                sort: {
                    value: {
                        fields: gqlSorting,
                    },
                    type: `${bocoreType}SortInput`,
                },
                filter: {
                    value: gqlFilter,
                    type: `${bocoreType}FilterInput`,
                },
            },
            fields: [
                {
                    records: additionalGqlFields?.length ? [...fieldRecords, ...additionalGqlFields] : fieldRecords,
                },
                "recordsCount",
            ],
        });
        return graphQlRequest(query, false)
            .then((response) => {
                if (bocoreType) {
                    const gqlResponseData = response.data.data[bocoreType];
                    // dispatch({
                    //     type: actions.NEW_DATA_FETCHED_PRO_GRID,
                    //     newGridData: { startRow: params?.request?.startRow, endRow: params?.request?.endRow },
                    // });
                    dispatch(uiActions.setNewGridDataFetched({ startRow: params?.request?.startRow, endRow: params?.request?.endRow }));

                    params.api.setGridOption("context", { ...params.api.getGridOption("context"), totalElementsCount: gqlResponseData?.recordsCount });

                    return params.success({
                        rowData: gqlResponseData?.records,
                        rowCount: gqlResponseData?.recordsCount,
                    });
                }
                return null;
            })
            .catch(() => {
                params.fail();
            });
    };

    const createDatasource = () => {
        return {
            getRows: (params: IServerSideGetRowsParams) => {
                if (dataFetchingType === "gql") {
                    gqlFetching(params);
                } else {
                    restApiFetching(params);
                }
            },
        };
    };

    const onGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);
        if (initialFilters) {
            e.api.sizeColumnsToFit();
            e.api.setFilterModel(initialFilters);
        }
        e.api.sizeColumnsToFit();

        if (dataFetchingType === "gql" || (dataFetchingType === "restApi" && !restApiDataClientSide)) {
            e.api.setGridOption("serverSideDatasource", createDatasource());
            // e.api.setServerSideDatasource(createDatasource());
        } else if (dataFetchingType === "restApi" && restApiDataClientSide) {
            restApiFetchingClientSide(e.api);
        } else {
            e.api.setGridOption("context", { ...gridApi?.getGridOption("context"), totalElementsCount: data?.length });
            setClientSideRowData(data);
        }

        const filterModel = e.api.getFilterModel();
        const pivotMode = e.api.isPivotMode();
        const columnState = e.api.getColumnState();
        const columnGroupState = e.api.getColumnGroupState();

        setDefaultGridState({
            pivotMode,
            columnState,
            columnGroupState,
            filterModel,
        });
    };

    const onGridPreDestroyed = (_gridEvent: GridPreDestroyedEvent) => {
        // Fix for React 19, we should check in the next release is that working as before.
        if (!gridApi) {
            return;
        }

        setGridApi(null);
        setGridAttributes([]);
        setFieldRecords([]);
        setClientSideRowData([]);
    };

    const onModelUpdated = () => {
        // This is intentional
    };

    const columnListener = (gridEvent: AgGridEvent) => {
        const columnState = gridEvent.api.getColumnState();
        const columnGroupState = gridEvent.api.getColumnGroupState();

        setActiveGridState((o) => ({ ...o, columnState, columnGroupState }));
    };

    const onColumnVisible = (gridEvent: ColumnVisibleEvent) => {
        // const visibleColumnKeys = gridEvent.api.getAllDisplayedColumns().map((c) => c.getColId());

        // const newGridAttributes = gridAttributes.map((attribute) => {
        //     const shallowAttribute = attribute;
        //     if (visibleColumnKeys.includes(attribute.field)) {
        //         shallowAttribute.hide = false;
        //     } else {
        //         shallowAttribute.hide = true;
        //     }
        //     return shallowAttribute;
        // });

        // setGridAttributes(newGridAttributes);

        // gridEvent.api.sizeColumnsToFit();
        // dispatch(uiActions.setForceRefreshGrid(true))
        columnListener(gridEvent);
    };

    // const onSelectionChanged = useCallback(() => {}, []);

    const onFilterChanged = (gridEvent: FilterChangedEvent) => {
        const filterModel = gridEvent.api.getFilterModel();

        setActiveGridState((o) => ({ ...o, filterModel }));
    };

    const onFirstDataRendered = (gridEvent: FirstDataRenderedEvent) => {
        const filterModel = gridEvent.api.getFilterModel();
        const pivotMode = gridEvent.api.isPivotMode();
        const columnState = gridEvent.api.getColumnState();
        const columnGroupState = gridEvent.api.getColumnGroupState();

        setActiveGridState({
            pivotMode,
            columnState,
            columnGroupState,
            filterModel,
        });
    };

    const listeners = useMemo(
        () => ({
            onFirstDataRendered,
            onFilterChanged,
            onSortChanged: columnListener,
            onColumnPinned: columnListener,
            onColumnResized: columnListener,
            onColumnMoved: columnListener,
            onColumnRowGroupChanged: columnListener,
            onColumnValueChanged: columnListener,
            onColumnPivotChanged: columnListener,

            onGridReady,
            onModelUpdated,
            onColumnVisible,
            onGridPreDestroyed,
            // onSelectionChanged,
        }),
        [signal, gridAttributes, incomingGridState],
    );

    useEffect(() => {
        gridApi?.setGridOption("context", { ...gridApi.getGridOption("context"), totalElementsCount: data?.length });
        setClientSideRowData(data);
    }, [data]);

    useEffect(() => {
        if (!shouldGridRefresh) {
            return;
        }

        if (gridApi === null) {
            dispatch(uiActions.setForceRefreshGrid(false));
            return;
        }
        // deselect all selected rows before refresh;
        gridApi.deselectAll();

        if (dataFetchingType === "gql" || (dataFetchingType === "restApi" && !restApiDataClientSide)) {
            gridApi.refreshServerSide({ purge: true });
            dispatch(uiActions.setForceRefreshGrid(false));
        } else if (dataFetchingType === "restApi" && restApiDataClientSide) {
            if (restApiRequest) {
                restApiRequest({ signal })
                    .then((res: any) => {
                        dispatch(uiActions.setForceRefreshGrid(false));
                        gridApi?.setGridOption("context", { ...gridApi.getGridOption("context"), totalElementsCount: res?.data?.totalElements });
                        setClientSideRowData(res?.data?.data);
                    })
                    .catch(() => {
                        toastError(<FormattedMessage id="common.error.api-message" defaultMessage="Something went wrong!" />);
                    });
            }
        } else {
            setClientSideRowData(data);
            dispatch(uiActions.setForceRefreshGrid(false));
        }
    }, [shouldGridRefresh]);

    const buildBocoreAttributeColumn = (attr: any) => {
        let customColumnOptions;
        if (columnOptions && columnOptions.length > 0) {
            customColumnOptions = columnOptions?.find((col: any) => col?.key === attr.attrKey);
        }
        return {
            field: attr?.attrKey,
            headerName: attr?.name,
            cellRenderer: (value: any, params: any) => getCellRendererByType(value, params.data, attr?.atrbType, attr?.attrKey),
            filter: getFilter(attr?.atrbType),
            filterParams: {
                ...(attr?.atrbType === "boolean" && { values: ["Yes", "No"] }),
                ...(attr?.atrbType === "value list" && {
                    values: attr?.attributeValues?.map((attribute: any) => attribute?.name).filter((name: string) => name !== BO_CORE_DEFAULT_VALUE),
                }),
                ...((attr?.atrbType === "date" || attr?.atrbType === "timestamp") && {
                    filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
                }),
            },
            floatingFilter: true,
            minWidth: 120,
            hide: initialShownColumns ? !initialShownColumns?.includes(attr?.attrKey) : false,
            headerComponentParams: { innerHeaderComponent: ColumnHeaderCopyWrapper },
            ...customColumnOptions?.options,
        };
    };

    const buildOtherColumnDef = (col: any) => ({
        field: col.key,
        headerName: col.title,
        cellRenderer: (value: any, params: any) => {
            if (col?.cellRenderer) {
                return col?.cellRenderer(value, params.data);
            }
            return getCellRendererByType(value, params.data, col.type, col.key);
        },
        filter: getFilter(col.type),
        filterParams: {
            ...(col.type === "boolean" && { values: ["Yes", "No"] }),
            ...(col.type === "value list" && {
                values: col?.attributeValues?.map((attribute: any) => attribute?.name).filter((name: string) => name !== BO_CORE_DEFAULT_VALUE),
            }),
            ...((col?.type === "date" || col?.type === "timestamp") && {
                filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
            }),
        },
        floatingFilter: true,
        minWidth: 120,
        hide: initialShownColumns ? !initialShownColumns?.includes(col.key) : false,
        ...(col?.columnOptions && col.columnOptions),
        headerComponentParams: { innerHeaderComponent: ColumnHeaderCopyWrapper },
    });

    const processBocoreTypeResponse = (res: any) => {
        if (!res?.data?.attributes) {
            return;
        }
        const gqlRecords: any = [];
        let gridConvertedAttributes: any = [];
        res.data.attributes.forEach((attr: any) => {
            if (ignoreBocoreAttributes?.some((attribute: any) => attribute === attr?.attrKey)) {
                return;
            }
            if (attr?.atrbType === "lookup") {
                gqlRecords.push({ [attr.attrKey]: ["id", "value"] });
            } else {
                gqlRecords.push(attr?.attrKey);
            }
            gridConvertedAttributes.push(buildBocoreAttributeColumn(attr));
        });
        if (otherColumns && otherColumns.length > 0) {
            otherColumns.forEach((col: any) => {
                if (col?.existing !== false) {
                    gqlRecords.push(col.key);
                }
                gridConvertedAttributes.push(buildOtherColumnDef(col));
            });
            setGridAttributes(gridConvertedAttributes);
        }
        if (initialShownColumns) {
            const hiddenAttributes = gridConvertedAttributes.filter((attribute: any) => attribute?.hide === true);
            const sortedGridAttributes = initialShownColumns.map((attribute: any) => {
                return gridConvertedAttributes.find((attr: any) => attr?.field === attribute);
            });
            gridConvertedAttributes = [...sortedGridAttributes, ...hiddenAttributes];
        }
        gqlRecords.push("id");
        setFieldRecords(gqlRecords);
        appendStandardGridColumns(gridConvertedAttributes, {
            showIdColumn,
            disableOptionMenu,
            dataFetchingType,
            editLink,
            editLinkNewTab,
            editLinkQueryParams,
            navigate,
        });
        setGridAttributes(gridConvertedAttributes);
    };

    useEffect(() => {
        if (customTypeDefinition && dataFetchingType !== "gql") {
            const hasMasterRow = typeof customGridOptions?.isRowMaster === "function";
            const { gridConvertedAttributes, records } = generatingGridColumns(
                customTypeDefinition,
                showIdColumn,
                initialShownColumns,
                navigate,
                editLink,
                editLinkNewTab,
                editLinkQueryParams,
                additionalLinkColumns,
                dataFetchingType,
                // customCellRenderers,
                hasMasterRow,
                columnOptions,
                restApiDataClientSide,
                // rowDragging,
                // disableRowSelecting,
                disableFullTextSearch,
                disableOptionMenu,
            );

            setFieldRecords(records);
            setGridAttributes(gridConvertedAttributes);
        } else if (bocoreType) {
            getTypeByKey(bocoreType, true, { signal }).then(processBocoreTypeResponse).catch(isCancelledErrorProcessor);
        } else {
            throw TypeError('"bocoreType" or "customTypeDefinition" must be provided!');
        }
    }, [signal]);

    useEffect(() => {
        // needed to make sure if reference to api fetching method is changed
        if (gridApi === null || restApiDataClientSide || dataFetchingType === "localData") {
            return;
        }

        gridApi.setGridOption("serverSideDatasource", createDatasource());
        // gridApi.setServerSideDatasource(createDatasource());
        // gridApi.refreshServerSide({ purge: true });
    }, [restApiRequest, gridApi]);

    return (
        <Root>
            {enableOrderCommentsFlyIn && (
                <GridCommentFlyIn openCommentFlyIn={openComment} closeFlyIn={handleCloseCommentFlyIn} commentJourneyId={Number(commentJourneyId)} />
            )}
            <HeaderAndButton
                dataFetchingType={dataFetchingType}
                gridApi={gridApi}
                tableTitle={tableTitle}
                restApiDataClientSide={restApiDataClientSide}
                editLink={editLink}
                editLinkQueryParams={editLinkQueryParams}
                deleteButtonFunc={deleteButtonFunc}
                deactivateButtonFunc={deactivateButtonFunc}
                setClientSideRowData={setClientSideRowData}
                restApiRequest={restApiRequest}
                deactivateButtonActivationChecker={deactivateButtonActivationChecker}
                disableFullTextSearch={disableFullTextSearch}
                additionalHamburgerMenuItems={additionalHamburgerMenuItems}
                disableEditing={disableEditing}
                disableExporting={disableExporting}
                // valueTypeConverterColumns={valueTypeConverterColumns}
                disableHeaderHamburgerMenu={disableHeaderHamburgerMenu}
                enableUATOrderActions={enableUATOrderActions}
                enableUATJourneyActions={enableUATJourneyActions}
                enableViewSelector={enableViewSelector}
                commentContextMenuAction={commentContextMenuAction}
                enableOrderCommentsFlyIn={enableOrderCommentsFlyIn}
                fullTextSearchLabel={fullTextSearchLabel}
                fullTextSearchPlaceholder={fullTextSearchPlaceholder}
                exportIdentifier={exportIdentifier}
                requestFilterProcessorV3={requestFilterProcessorV3}
                initialFilters={initialFilters}
                dynamicFilters={dynamicFilters}
                hasUserFilterableColumns={hasUserFilterableColumns}
            />
            {fieldRecords?.length > 0 ? (
                <AgGrid
                    rowDragging={rowDragging}
                    restApiDataClientSide={restApiDataClientSide}
                    rowData={clientSideRowData}
                    suppressMenuHide
                    dataFetchingType={dataFetchingType}
                    columnArray={gridAttributes}
                    pageSize={pageSize}
                    gridHeightCss={gridHeightCss}
                    customGridOptions={customGridOptions}
                    turnOffManageColumns={turnOffManageColumns}
                    eventHandlers={listeners}
                    showOptionMenu={!disableOptionMenu}
                    editLink={editLink}
                    disableEditing={disableEditing}
                    editLinkQueryParams={editLinkQueryParams}
                    deleteButtonFunc={deleteButtonFunc}
                    deactivateButtonFunc={deactivateButtonFunc}
                    deactivateButtonActivationChecker={deactivateButtonActivationChecker}
                    enableUATOrderActions={enableUATOrderActions}
                    enableUATJourneyActions={enableUATJourneyActions}
                    commentContextMenuAction={commentContextMenuAction}
                    enableOrderCommentsFlyIn={enableOrderCommentsFlyIn}
                    disableRowSelecting={disableRowSelecting}
                    disableAllRowsSelecting={disableAllRowsSelecting}
                    showTotalCount={showTotalCount}
                    enablePageSizeSelector={enablePageSizeSelector}
                    availablePageSizes={availablePageSizes}
                    enableRefreshButton={enableRefreshButton}
                    disableExporting={disableExporting}
                />
            ) : null}
        </Root>
    );
};

// * renamed to enable autocomplete import and intellisence
const GenericProGrid: FC<PropsWithChildren<IProps>> = ({ gridIdentifier, ...props }) => {
    return (
        <GenericProGridProvider gridIdentifier={gridIdentifier}>
            <GenericProGridImplementation {...{ gridIdentifier, ...props }} />
        </GenericProGridProvider>
    );
};

// * renamed to enable autocomplete import and intellisence
export default GenericProGrid;
